/**
 *
 * Cloud Account Wizard
 *
 */

import React, { memo } from 'react';

import { CloudProviders } from '@ariksa/cloud-account/api';
import { Clients } from '@ariksa/notification';
import { Box, Stack } from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';

import { IdentityProviderName } from 'api/auth/api.pb';
import {
  Card,
  PageHeaderWithBackButton,
  StackedHeader,
} from 'components/DataDisplay';
import { DataSources } from 'containers/Setup/Integrations/Components/IntegrationsWizard/DataSources';
import { EmailTable } from 'containers/Setup/Integrations/Components/IntegrationsWizard/EmailGroup/EmailTable';
import { JiraTable } from 'containers/Setup/Integrations/Components/IntegrationsWizard/Jira/Components/JiraTable';
import { ServiceNowTable } from 'containers/Setup/Integrations/Components/IntegrationsWizard/ServiceNow/Components/ServiceNowTable';
import { SIEMTable } from 'containers/Setup/Integrations/Components/IntegrationsWizard/SIEM/SIEMTable';
import { SlackTable } from 'containers/Setup/Integrations/Components/IntegrationsWizard/Slack/Components/SlackTable';
import { SMSTable } from 'containers/Setup/Integrations/Components/IntegrationsWizard/SMS/SMSTable';
import { SSOProvidersTable } from 'containers/Setup/Integrations/Components/IntegrationsWizard/SSO/SSOProvidersTable';
import { TeamsTable } from 'containers/Setup/Integrations/Components/IntegrationsWizard/Teams/Components/TeamsTable';
import {
  getIntegrationsSubTitle,
  getIntegrationsTitle,
} from 'containers/Setup/Integrations/Components/util';

export const IntegrationsTable = memo(() => {
  const params = useParams<{
    provider: string;
  }>();
  const navigate = useNavigate();

  const getComponent = () => {
    switch (params?.provider) {
      case Clients.Elasticsearch:
        return <SIEMTable />;
      case Clients.Jira:
        return <JiraTable />;
      case Clients.Servicenow:
        return <ServiceNowTable />;
      case Clients.Slack:
        return <SlackTable />;
      case Clients.Teams:
        return <TeamsTable />;
      case Clients.Sns:
        return <SMSTable />;
      case IdentityProviderName.Okta:
      case IdentityProviderName.Google:
      case IdentityProviderName.Microsoft:
        return <SSOProvidersTable />;
      case Clients.Email:
        return <EmailTable />;
      case CloudProviders.Gitlab:
      case CloudProviders.Github:
      case CloudProviders.Bitbucket:
        return <DataSources />;
    }
  };

  return (
    <Card>
      <Stack spacing={5} h="full" w="full">
        <Box w="full">
          <PageHeaderWithBackButton
            label={
              <Box w="full">
                <StackedHeader
                  upper={getIntegrationsTitle(params?.provider)}
                  lower={getIntegrationsSubTitle(params?.provider)}
                  titleWidth="full"
                />
              </Box>
            }
            iconType={params?.provider}
            iconFilled={false}
            iconSize="9"
            onClickBack={() => navigate('/setup/integrations')}
            buttonSpacing={28}
            spacing={8}
            iconColor="primary"
          />
        </Box>
        <Box pl={32}>
          <Box borderBottom="1px solid" borderColor="gray.200" pl={4}></Box>
        </Box>
        <Box h="full" w="full" pl={32}>
          {getComponent()}
        </Box>
      </Stack>
    </Card>
  );
});
